import React from "react";
import { Button, Card, ControlGroup, Elevation, H4, HTMLSelect, Tag, Text, Icon, FormGroup, MenuItem } from "@blueprintjs/core";
import { Suggest } from "@blueprintjs/select";
import { inject, observer } from "mobx-react";
import cogoToast from "cogo-toast";
import _ from "lodash";
import { apiCallPost, apiCallGet } from "../../micro/fns/util";
import Modal from "../../micro/components/Modal";
import SingleSelectWrapper from "../../micro/utils/SingleSelectWrapper";
import AsyncSelWrap from "../../micro/utils/AsyncSelWrap";
import { indianRupee } from "../../micro/fns/util";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getSxPriceEstimatePdfDefinition } from "./SxPriceEstimatorPdf";

// Set up fonts for PDFMake - matching pattern from printDoc.js
try {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;
} catch (e) {
  console.error("Error setting up pdfMake vfs", e);
  // Fallback
  pdfMake.vfs = pdfFonts;
}

class SxPriceEstimator extends React.Component {
  state = {
    isOpen: false,
    isLoadingSurgeryMaster: false,
    isLoadingAnesthesia: false,
    isLoadingPayorPanels: false,
    surgeryMasterOptions: [], // { id, label, record }
    anesthesiaOptions: [], // { id, name, rate }
    payorPanels: [], // { name, group } - loaded from Payment Panel Master
    selectedPatient: null, // { _id, name, pid, age, gender }
    patientSearchResults: [], // Search results for AsyncSelWrap
    isSearchingPatients: false,
    estimator: {
      payor: "",
      selectedAnesthesiaId: "",
      selectedSurgeries: [],
    },
    surgeryRows: [], // { id, laterality, estimatedCost }
    surgerySearchQueries: {}, // { rowIndex: queryString }
  };

  open = async () => {
    // Open modal immediately for better UX
    this.setState({ isOpen: true });

    // Load data in background
    try {
      await Promise.all([
        this.loadSurgeryMasterOptions(),
        this.loadAnesthesiaOptions(),
        this.loadPayorPanels()
      ]);
    } catch (e) {
      console.error("Error loading estimator data:", e);
    }
  };

  close = () => {
    this.setState({
      isOpen: false,
      estimator: { payor: "", selectedAnesthesiaId: "", selectedSurgeries: [] },
      surgeryRows: [],
      selectedPatient: null,
      patientSearchResults: [],
      isSearchingPatients: false,
    });
  };

  // ----- Helpers derived from SurgeryBooking "Surgery Details" -----
  getPrintedNames = () => {
    const payor = this.state.estimator.payor;
    const opts = this.state.surgeryMasterOptions || [];
    const filtered = payor ? opts.filter((o) => (o?.record?.payor || o?.record?.group || "") === payor) : opts;
    return filtered.map((o) => ({
      _id: o.id,
      printedName: o.record.printedName || o.record.surgeryName || "Surgery",
      surgeryName: o.record.surgeryName || o.record.printedName || "Surgery",
      group: o.record.group || o.record.payor || "General",
      surgeryRate: Number(o.record.surgeryRate || 0),
      mouDiscount: Number(o.record.mouDiscount || 0),
      specialDiscount: Number(o.record.specialDiscount || 0),
    }));
  };

  createPayorPanelItems = () => {
    try {
      // First try to use dedicated payor panels from Insurance Panel Master
      if (this.state.payorPanels && this.state.payorPanels.length > 0) {
        return this.state.payorPanels
          .map(p => p.panelName)
          .filter(Boolean)
          .sort();
      }
      // Fallback to extracting from surgery pricing master
      const items = Array.from(
        new Set((this.state.surgeryMasterOptions || []).map((o) => o?.record?.payor || o?.record?.group || "General"))
      )
        .filter(Boolean)
        .sort();
      return items;
    } catch (e) {
      return [];
    }
  };

  createSurgeryItems = () => {
    return this.getPrintedNames().map((s) => s.printedName);
  };

  // Filter and sort surgeries based on query - similar to SurgeryBooking.js
  getFilteredSurgeries = (query) => {
    const surgeries = this.getPrintedNames() || [];
    if (!query || !query.trim()) {
      return surgeries.sort((a, b) => (a.printedName || "").localeCompare(b.printedName || ""));
    }
    const searchTerm = query.toLowerCase().trim();
    return surgeries
      .filter((s) => (s.printedName || "").toLowerCase().includes(searchTerm))
      .sort((a, b) => {
        const aName = (a.printedName || "").toLowerCase();
        const bName = (b.printedName || "").toLowerCase();
        // Prioritize items that start with the search term
        const aStartsWith = aName.startsWith(searchTerm);
        const bStartsWith = bName.startsWith(searchTerm);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return aName.localeCompare(bName);
      });
  };

  // Highlight search text in surgery names
  highlightSearchText = (text, query) => {
    if (!query) return text;
    const searchTerm = query.toLowerCase().trim();
    const textLower = (text || "").toLowerCase();
    if (!textLower.includes(searchTerm)) return text;
    const index = textLower.indexOf(searchTerm);
    if (index === -1) return text;
    const before = text.slice(0, index);
    const match = text.slice(index, index + searchTerm.length);
    const after = text.slice(index + searchTerm.length);
    return (
      <span>
        {before}
        <strong style={{ backgroundColor: "rgba(19, 124, 189, 0.3)" }}>{match}</strong>
        {after}
      </span>
    );
  };

  createLateralityItems = () => ["Left Eye", "Right Eye", "Both Eyes"];

  createAnesthesiaItems = () => (this.state.anesthesiaOptions || []).map((o) => o.name);

  getAnesthesiaRate = (anesthesiaName) => {
    const opt = (this.state.anesthesiaOptions || []).find((o) => o.name === anesthesiaName || o.id === anesthesiaName);
    return Number(opt?.rate || 0);
  };

  // Patient search API for AsyncSelWrap (using same API as SurgeryBooking)
  searchPatients = async (query) => {
    if (!query || query.length < 2) {
      this.setState({ patientSearchResults: [], isSearchingPatients: false });
      return;
    }

    this.setState({ isSearchingPatients: true });

    try {
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/search`, {
        prefix: query,
      });

      // Format results to match AsyncSelWrap expected structure
      const results = (response || []).map((each) => ({
        item: _.startCase(_.lowerCase(each.name)),
        entityTypeString: each.genericPid,
        helper: each.phone,
        helper2: each.dob ? new Date().getFullYear() - new Date(each.dob).getFullYear() : null,
        patientData: each,
      }));

      this.setState({ patientSearchResults: results, isSearchingPatients: false });
    } catch (err) {
      console.error("Error searching patients:", err);
      this.setState({ patientSearchResults: [], isSearchingPatients: false });
    }
  };

  handlePatientSelect = (patient) => {
    // Store the selected patient with normalized data
    this.setState({
      selectedPatient: {
        _id: patient.patientData?.genericPid || patient.entityTypeString,
        name: patient.item,
        pid: patient.entityTypeString,
        age: patient.helper2,
        phone: patient.helper,
        patientData: patient.patientData,
      }
    });
  };

  handlePayorPanelSelect = (selectedPanel) => {
    this.setState({ estimator: { ...this.state.estimator, payor: selectedPanel }, surgeryRows: [] });
  };

  getLastSelectedLaterality = () => {
    const rows = Array.isArray(this.state.surgeryRows) ? this.state.surgeryRows : [];
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const laterality = rows[i]?.laterality;
      if (laterality) {
        return laterality;
      }
    }
    return null;
  };

  addSurgeryRow = () => {
    const defaultLaterality = this.getLastSelectedLaterality();
    this.setState({
      surgeryRows: [
        ...(this.state.surgeryRows || []),
        { id: null, laterality: defaultLaterality, estimatedCost: 0 },
      ],
    });
  };

  removeSurgeryRow = (index) => {
    this.setState({ surgeryRows: (this.state.surgeryRows || []).filter((_, i) => i !== index) });
  };

  handleSurgerySelect = (selectedSurgeryPrintedName, rowIndex) => {
    const printed = this.getPrintedNames();
    const surgery = printed.find((s) => s.printedName === selectedSurgeryPrintedName);
    const rows = [...(this.state.surgeryRows || [])];
    if (rows[rowIndex]) {
      rows[rowIndex].id = surgery ? surgery._id : null;
      rows[rowIndex].estimatedCost = this.calculateEstimatedCost(rows[rowIndex].id);
      this.setState({ surgeryRows: rows });
    }
  };

  handleLateralitySelect = (selectedLaterality, rowIndex) => {
    const rows = [...(this.state.surgeryRows || [])];
    if (rows[rowIndex]) {
      const map = { "Left Eye": "left", "Right Eye": "right", "Both Eyes": "both" };
      rows[rowIndex].laterality = map[selectedLaterality] || null;
      this.setState({ surgeryRows: rows });
    }
  };

  calculateEstimatedCost = (surgeryId) => {
    if (!surgeryId) {
      return 0;
    }
    const printed = this.getPrintedNames();
    const surgery = printed.find((x) => x._id === surgeryId);
    if (!surgery) {
      return 0;
    }
    const surgeryRate = Number(surgery.surgeryRate || 0);
    const mouDiscountAmount = (Number(surgery.mouDiscount || 0) / 100) * surgeryRate || 0;
    const specialDiscountAmount = Number(surgery.specialDiscount || 0);
    const estimated = Math.max(0, Math.round(surgeryRate - mouDiscountAmount - specialDiscountAmount));
    return estimated;
  };

  computeConsolidatedGrandTotal = () => {
    const totalSurgeryCosts = (this.state.surgeryRows || []).reduce(
      (sum, row) => sum + Number(this.calculateEstimatedCost(row.id) || 0),
      0
    );
    const anesthesiaRate = this.getAnesthesiaRate(this.state.estimator.selectedAnesthesiaId);
    return Math.max(0, Math.round(totalSurgeryCosts + Number(anesthesiaRate || 0)));
  };

  loadSurgeryMasterOptions = async () => {
    if (this.state.isLoadingSurgeryMaster || this.state.surgeryMasterOptions.length) return;
    try {
      this.setState({ isLoadingSurgeryMaster: true });

      // Use the same API as SurgeryBooking.js - neoSxPaymentMaster
      const response = await apiCallGet(
        `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/getAll?limit=10000&offset=0&isActive=true`
      );

      // Handle the API response structure - API returns items array
      const printedNamesData = response?.data?.items || [];

      // Transform for dropdown - include payor information and financial data
      const options = printedNamesData.map((item) => ({
        id: item._id,
        label: `${item.printedName || item.surgeryName} - ${item.payor || 'General'}`,
        record: {
          _id: item._id,
          printedName: item.printedName || item.surgeryName,
          surgeryName: item.surgeryName || item.printedName,
          payor: item.payor,
          group: item.group,
          // Financial data from surgery payment master
          surgeryRate: item.surgeryRate || 0,
          anaestheticCharges: item.anaestheticCharges || 0,
          tissueProcessing: item.tissueProcessing || 0,
          rate: item.rate || 0,
          taxApplicable: item.taxApplicable || 0,
          implantTax: item.implantTax || 0,
          procedureCost: item.procedureCost || 0,
          implantCost: item.implantCost || 0,
          femto: item.femto || 0,
          specialDiscount: item.specialDiscount || 0,
          mouDiscount: item.mouDiscount || 0,
          iolDiscount: item.iolDiscount || 0,
        }
      }));

      this.setState({ surgeryMasterOptions: options, isLoadingSurgeryMaster: false });
    } catch (err) {
      this.setState({ isLoadingSurgeryMaster: false });
      cogoToast.error("Failed to load surgeries for estimator");
    }
  };

  loadAnesthesiaOptions = async () => {
    if (this.state.isLoadingAnesthesia || this.state.anesthesiaOptions.length) return;
    try {
      this.setState({ isLoadingAnesthesia: true });
      const res = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/getAll`, {});
      const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const mapped = (arr || [])
        .filter((item) => item && typeof item.name === "string" && item.name.trim().length > 0)
        .map((item) => ({ id: item.name, name: item.name, rate: item.rate || 0 }));
      this.setState({ anesthesiaOptions: mapped, isLoadingAnesthesia: false });
    } catch (err) {
      this.setState({ isLoadingAnesthesia: false });
      cogoToast.error("Failed to load anesthesia options");
    }
  };

  loadPayorPanels = async () => {
    if (this.state.isLoadingPayorPanels || this.state.payorPanels.length) return;
    try {
      this.setState({ isLoadingPayorPanels: true });
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/getAll`, {});

      // Handle the API response structure - same as SurgeryBooking.js
      const payorPanelsData = response?.data || response || [];

      // Transform for dropdown - extract panelName
      const payorPanels = payorPanelsData.map((item) => ({
        _id: item._id,
        panelName: item.panelName,
      }));

      this.setState({ payorPanels, isLoadingPayorPanels: false });
    } catch (err) {
      this.setState({ isLoadingPayorPanels: false });
      console.warn("Failed to load payor panels from Insurance Panel Master, falling back to surgery pricing data");
    }
  };

  getPayorItems = () => {
    try {
      const items = Array.from(
        new Set((this.state.surgeryMasterOptions || []).map((o) => o?.record?.payor || o?.record?.group || "General"))
      )
        .filter(Boolean)
        .sort();
      return items;
    } catch (e) {
      return [];
    }
  };

  getSurgeryItems = () => {
    const payor = this.state.estimator.payor;
    const opts = this.state.surgeryMasterOptions || [];
    const filtered = payor ? opts.filter((o) => (o?.record?.payor || o?.record?.group || "") === payor) : opts;
    return filtered.map((o) => o.label);
  };

  addSurgeryByLabel = (label) => {
    const option = (this.state.surgeryMasterOptions || []).find((o) => o.label === label);
    if (!option) return;
    this.setState((s) => ({
      estimator: {
        ...s.estimator,
        selectedSurgeries: [...(s.estimator.selectedSurgeries || []), option.record],
      },
    }));
  };

  removeSurgeryAt = (idx) => {
    this.setState((s) => ({
      estimator: {
        ...s.estimator,
        selectedSurgeries: (s.estimator.selectedSurgeries || []).filter((_, i) => i !== idx),
      },
    }));
  };

  computeTotals = () => {
    const surgeries = this.state.estimator.selectedSurgeries || [];
    if (!surgeries.length) {
      return {
        packageCostPaise: 0,
        procedureTaxPaise: 0,
        procedureCostPaise: 0,
        femtoChargesPaise: 0,
        mouDiscountPaise: 0,
        iolDiscountPaise: 0,
        specialDiscountPaise: 0,
        surgerySubtotalPaise: 0,
        implantInclusiveMinusIolPaise: 0,
        grandTotalPaise: 0,
        procTaxPercent: 0,
        implantTaxPercent: 0,
        mouDiscountPercent: 0,
      };
    }

    const toPaise = (n) => Math.trunc((Number(n) || 0) * 100);
    const sumPaise = (arr, key) => arr.reduce((sum, r) => sum + toPaise(r?.[key] != null ? r[key] : 0), 0);

    const surgeryRatePaise = sumPaise(surgeries, "surgeryRate");
    const tissueProcessingPaise = sumPaise(surgeries, "tissueProcessing");
    const procedureCostPaiseSum = sumPaise(surgeries, "procedureCost");
    const implantCostPaise = sumPaise(surgeries, "implantCost");
    const femtoChargesPaise = sumPaise(surgeries, "femto");
    const specialDiscountPaise = sumPaise(surgeries, "specialDiscount");
    const iolDiscountPaise = sumPaise(surgeries, "iolDiscount");

    const mouList = surgeries.map((r) => Number(r?.mouDiscount || 0));
    const mouDiscountPercent = mouList.length && mouList.every((v) => v === mouList[0]) ? mouList[0] : 0;

    const procTaxPercent = Number(surgeries[0]?.taxApplicable || 0);
    const implantTaxPercent = Number(surgeries[0]?.implantTax || 0);

    const anesthesiaId = this.state.estimator.selectedAnesthesiaId || "";
    const anesthesiaOption = (this.state.anesthesiaOptions || []).find((a) => a.id === anesthesiaId);
    const anaestheticChargesPaise = anesthesiaOption ? toPaise(anesthesiaOption.rate || 0) : 0;

    const packageCostRupees = (surgeryRatePaise + anaestheticChargesPaise + tissueProcessingPaise) / 100;
    const procedureTaxRupees = (packageCostRupees * procTaxPercent) / 100;

    const hasExplicitProc = surgeries.some((r) => Number(r?.procedureCost || 0) > 0);
    const procedureCostRupees = hasExplicitProc ? procedureCostPaiseSum / 100 : packageCostRupees + procedureTaxRupees;

    const mouDiscountRupees = (Number(mouDiscountPercent || 0) * packageCostRupees) / 100;

    const surgerySubtotalRupees =
      packageCostRupees +
      femtoChargesPaise / 100 -
      mouDiscountRupees -
      iolDiscountPaise / 100 -
      specialDiscountPaise / 100;

    const implantInclusiveMinusIolPaise = Math.max(0, implantCostPaise - iolDiscountPaise);

    const surgerySubtotalPaise = Math.max(0, Math.trunc(surgerySubtotalRupees * 100));
    const procedureTaxPaise = Math.max(0, Math.trunc(procedureTaxRupees * 100));
    const procedureCostPaise = Math.max(0, Math.trunc(procedureCostRupees * 100));
    const mouDiscountPaise = Math.max(0, Math.trunc(mouDiscountRupees * 100));

    const grandTotalPaise = surgerySubtotalPaise + implantInclusiveMinusIolPaise;

    return {
      packageCostPaise: Math.max(0, surgeryRatePaise + anaestheticChargesPaise + tissueProcessingPaise),
      procedureTaxPaise,
      procedureCostPaise,
      femtoChargesPaise,
      mouDiscountPaise,
      iolDiscountPaise,
      specialDiscountPaise,
      surgerySubtotalPaise,
      implantInclusiveMinusIolPaise,
      grandTotalPaise,
      procTaxPercent,
      implantTaxPercent,
      mouDiscountPercent,
    };
  };

  printEstimate = () => {
    try {
      const { estimator, surgeryRows, surgeryMasterOptions, anesthesiaOptions } = this.state;
      const printedNames = this.getPrintedNames();
      const consolidatedTotal = this.computeConsolidatedGrandTotal();

      const anesthesiaName =
        (anesthesiaOptions || []).find((a) => a.id === estimator.selectedAnesthesiaId)?.name || "";
      const anesthesiaRate = this.getAnesthesiaRate(estimator.selectedAnesthesiaId);

      const surgeriesData = (surgeryRows || []).map((row) => {
        const surgery = printedNames.find((s) => s._id === row.id);
        if (!surgery) return null;

        const totalBefore = Number(surgery.surgeryRate || 0) + Number(anesthesiaRate || 0);
        const mouDiscountAmount = (Number(surgery.mouDiscount || 0) / 100) * totalBefore || 0;

        return {
          printedName: surgery.printedName,
          group: surgery.group,
          laterality:
            row.laterality === "left"
              ? "Left"
              : row.laterality === "right"
                ? "Right"
                : row.laterality === "both"
                  ? "Both"
                  : "-",
          surgeryRate: surgery.surgeryRate,
          anesthesiaCharges: anesthesiaRate,
          mouDiscountAmount: mouDiscountAmount,
          specialDiscount: surgery.specialDiscount,
          estimatedCost: this.calculateEstimatedCost(row.id),
        };
      }).filter(Boolean);

      const pdfData = {
        payor: estimator.payor,
        anesthesiaType: anesthesiaName,
        anesthesiaRate: anesthesiaRate,
        surgeries: surgeriesData,
        consolidatedTotal: consolidatedTotal,
        hospitalName: this.props.authStore?.hospitalName || "Eye Hospital",
        patientName: this.state.selectedPatient?.name || "",
        patientId: this.state.selectedPatient?.pid || "",
      };

      const docDefinition = getSxPriceEstimatePdfDefinition(pdfData);
      pdfMake.createPdf(docDefinition).open();
    } catch (e) {
      console.error("Print error:", e);
      cogoToast.error("Failed to generate PDF");
    }
  };

  renderModal = () => {
    const payorItems = this.createPayorPanelItems();
    const anesthesiaItems = this.createAnesthesiaItems();
    const printedNames = this.getPrintedNames();
    const consolidated = this.computeConsolidatedGrandTotal();
    return (
      <Modal
        isOpen={this.state.isOpen}
        onClose={this.close}
        title="Sx Price Estimator"
        primaryButtonText="Close"
        width="1000px"
        className="bp3-dark"
        bodyStyle={{ backgroundColor: "var(--bg-primary)", color: "var(--text-primary)" }}
      >
        <div style={{ display: "grid", gap: 12 }}>
          {/* Patient Selection */}
          <FormGroup label="Patient (Optional)" style={{ margin: 0 }}>
            <AsyncSelWrap
              items={this.state.patientSearchResults}
              searching={this.state.isSearchingPatients}
              selectedItem={this.state.selectedPatient}
              update={(query) => this.searchPatients(query)}
              onChange={(patient) => this.handlePatientSelect(patient)}
              placeholder="Search by PID or Name..."
              width="100%"
              popoverProps={{
                minimal: true,
                usePortal: true,
                position: "bottom-left",
                modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } }
              }}
            />
            {this.state.selectedPatient && (
              <div style={{ marginTop: 8, padding: 8, backgroundColor: "rgba(19, 124, 189, 0.1)", borderRadius: 4 }}>
                <Text style={{ fontSize: 12 }}>
                  <strong>{this.state.selectedPatient.pid}</strong> - {this.state.selectedPatient.name}
                  {this.state.selectedPatient.age && ` • ${this.state.selectedPatient.age} years`}
                  {this.state.selectedPatient.phone && ` • ${this.state.selectedPatient.phone}`}
                </Text>
              </div>
            )}
          </FormGroup>

          {/* Row: Payor Panel and Anesthesia (to support cost calc like details section) */}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <FormGroup label="Payor Panel" style={{ minWidth: 280, margin: 0 }}>
              <SingleSelectWrapper
                items={payorItems}
                selectedItem={this.state.estimator.payor || null}
                placeholder={
                  this.state.isLoadingSurgeryMaster || this.state.isLoadingPayorPanels
                    ? "Loading payor panels..."
                    : "Search payor panels..."
                }
                disabled={this.state.isLoadingSurgeryMaster || this.state.isLoadingPayorPanels}
                onChange={this.handlePayorPanelSelect}
                width="280px"
                inputProps={{
                  className: "v3-select",
                  onFocus: this.loadPayorPanels,
                  leftIcon: this.state.isLoadingPayorPanels ? "refresh" : "search"
                }}
                popoverProps={{
                  minimal: true,
                  usePortal: true,
                  position: "bottom-left",
                  modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } }
                }}
              />
            </FormGroup>
            <FormGroup label="Anesthesia Type" style={{ minWidth: 260, margin: 0 }}>
              <SingleSelectWrapper
                items={anesthesiaItems}
                selectedItem={this.state.estimator.selectedAnesthesiaId || null}
                placeholder={
                  this.state.isLoadingAnesthesia ? "Loading anesthesia types..." : "Search anesthesia types..."
                }
                disabled={this.state.isLoadingAnesthesia}
                onChange={(val) => this.setState({ estimator: { ...this.state.estimator, selectedAnesthesiaId: val } })}
                width="260px"
                inputProps={{ className: "v3-select", onFocus: this.loadAnesthesiaOptions }}
                popoverProps={{
                  minimal: true,
                  usePortal: true,
                  position: "bottom-left",
                  modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } }
                }}
              />
            </FormGroup>
          </div>

          {/* Selected Surgeries section (from Surgery Details) */}
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              backgroundColor: "rgba(19, 124, 189, 0.06)",
              border: "1px solid rgba(19, 124, 189, 0.3)",
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(19, 124, 189, 0.15)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <H4 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon icon="list" size={14} color="#137cbd" /> Selected Surgeries
                {this.state.estimator.payor && (
                  <span className="v3-text-secondary"> (Filtered by {this.state.estimator.payor})</span>
                )}
              </H4>
              <Button
                small
                intent="primary"
                icon="add"
                onClick={this.addSurgeryRow}
                disabled={!this.state.estimator.payor}
              >
                Add Surgery
              </Button>
            </div>
            {!this.state.estimator.payor && (
              <Text className="v3-text-secondary" style={{ fontStyle: "italic", marginBottom: 12, display: "block" }}>
                Select a Payor Panel to add surgeries
              </Text>
            )}

            {(this.state.surgeryRows || []).length === 0 ? (
              <div
                style={{
                  padding: 20,
                  textAlign: "center",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: 6,
                  border: "1px dashed var(--border-color)",
                }}
              >
                <Text className="v3-text-secondary">No surgeries added yet.</Text>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {(this.state.surgeryRows || []).map((row, idx) => {
                  const surgery = printedNames.find((s) => s._id === row.id);
                  const anesthesiaRate = this.getAnesthesiaRate(this.state.estimator.selectedAnesthesiaId);
                  return (
                    <Card
                      key={`est-row-${idx}`}
                      style={{
                        backgroundColor: "var(--bg-surface)",
                        border: "1px solid var(--border-color)",
                        borderRadius: 8,
                        padding: 16,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: 12,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                            <div style={{ flex: 3, minWidth: 0 }}>
                              <Suggest
                                items={this.getFilteredSurgeries(this.state.surgerySearchQueries[idx] || "")}
                                selectedItem={surgery}
                                inputValueRenderer={(item) => (item ? item.printedName : "")}
                                itemPredicate={(query, item) => {
                                  if (!query) return true;
                                  const searchTerm = query.toLowerCase().trim();
                                  const surgeryName = (item.printedName || "").toLowerCase();
                                  return surgeryName.includes(searchTerm);
                                }}
                                itemRenderer={(item, { handleClick, modifiers, query }) => {
                                  if (!modifiers.matchesPredicate) return null;
                                  return (
                                    <MenuItem
                                      key={item._id}
                                      active={modifiers.active}
                                      disabled={modifiers.disabled}
                                      onClick={handleClick}
                                      text={this.highlightSearchText(item.printedName, query)}
                                    />
                                  );
                                }}
                                onItemSelect={(selectedSurgery) => {
                                  this.handleSurgerySelect(selectedSurgery.printedName, idx);
                                }}
                                onQueryChange={(query) => {
                                  this.setState((prevState) => ({
                                    surgerySearchQueries: {
                                      ...prevState.surgerySearchQueries,
                                      [idx]: query,
                                    },
                                  }));
                                }}
                                noResults={<MenuItem disabled text="No surgeries found" />}
                                popoverProps={{
                                  minimal: true,
                                  usePortal: true,
                                  position: "bottom-left",
                                  modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } }
                                }}
                                inputProps={{
                                  large: true,
                                  className: "v3-select",
                                  style: { minWidth: "300px", width: "100%" },
                                  placeholder: this.state.estimator.payor
                                    ? "Search surgeries..."
                                    : "Select payor panel first",
                                  disabled: !this.state.estimator.payor,
                                  onFocus: this.loadSurgeryMasterOptions,
                                }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 0, marginLeft: 8 }}>
                              <SingleSelectWrapper
                                items={this.createLateralityItems()}
                                selectedItem={(() => {
                                  const lateralityMap = { left: "Left Eye", right: "Right Eye", both: "Both Eyes" };
                                  return lateralityMap[row.laterality] || null;
                                })()}
                                placeholder="Select eye..."
                                onChange={(selectedLaterality) => this.handleLateralitySelect(selectedLaterality, idx)}
                                width="100%"
                                noResultsText="No options found"
                                inputProps={{ large: true, className: "v3-select", style: { minWidth: "150px" } }}
                                popoverProps={{
                                  minimal: true,
                                  usePortal: true,
                                  position: "bottom-left",
                                  modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } }
                                }}
                              />
                            </div>
                            {surgery && surgery.group && (
                              <Tag
                                minimal
                                intent="primary"
                                style={{
                                  fontSize: "11px",
                                  backgroundColor: "rgba(19, 124, 189, 0.1)",
                                  color: "#137cbd",
                                  border: "1px solid rgba(19, 124, 189, 0.2)",
                                  flexShrink: 0,
                                  alignSelf: "flex-start",
                                }}
                              >
                                {surgery.group}
                              </Tag>
                            )}
                          </div>
                        </div>
                        <Button
                          minimal
                          small
                          icon="trash"
                          intent="danger"
                          onClick={() => this.removeSurgeryRow(idx)}
                          style={{ marginLeft: 12 }}
                        />
                      </div>

                      {surgery && (
                        <div
                          style={{
                            backgroundColor: "rgba(15, 153, 96, 0.05)",
                            border: "1px solid rgba(15, 153, 96, 0.2)",
                            borderRadius: 6,
                            padding: 12,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              marginBottom: 8,
                              color: "var(--text-secondary)",
                            }}
                          >
                            Cost Breakdown
                          </Text>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <div>
                              <Text style={{ fontSize: "11px", color: "var(--text-secondary)", margin: 0 }}>
                                Surgery Rate
                              </Text>
                              <Text
                                style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", margin: 0 }}
                              >
                                ₹ {(surgery.surgeryRate || 0).toLocaleString("en-IN")}
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: "11px", color: "var(--text-secondary)", margin: 0 }}>
                                Anesthesia Charges
                              </Text>
                              <Text
                                style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", margin: 0 }}
                              >
                                ₹ {Number(anesthesiaRate || 0).toLocaleString("en-IN")}
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: "11px", color: "var(--text-secondary)", margin: 0 }}>
                                MoU Discount ({surgery.mouDiscount || 0}%)
                              </Text>
                              <Text style={{ fontSize: "13px", fontWeight: 600, color: "#D9822B", margin: 0 }}>
                                -₹{" "}
                                {(() => {
                                  const totalBefore = Number(surgery.surgeryRate || 0) + Number(anesthesiaRate || 0);
                                  const disc = (Number(surgery.mouDiscount || 0) / 100) * totalBefore || 0;
                                  return Math.round(disc).toLocaleString("en-IN");
                                })()}
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: "11px", color: "var(--text-secondary)", margin: 0 }}>
                                Special Discount
                              </Text>
                              <Text style={{ fontSize: "13px", fontWeight: 600, color: "#D9822B", margin: 0 }}>
                                -₹ {Number(surgery.specialDiscount || 0).toLocaleString("en-IN")}
                              </Text>
                            </div>
                          </div>

                          <div style={{ borderTop: "1px solid rgba(15, 153, 96, 0.2)", paddingTop: 12 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                              }}
                            >
                              <Text
                                style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)", margin: 0 }}
                              >
                                Estimated Total
                              </Text>
                              <Text style={{ fontSize: "16px", fontWeight: 700, color: "#0F9960", margin: 0 }}>
                                ₹ {(() => this.calculateEstimatedCost(row.id)).call(null).toLocaleString("en-IN")}
                              </Text>
                            </div>
                            <div
                              style={{
                                backgroundColor: "rgba(255, 193, 7, 0.1)",
                                border: "1px solid rgba(255, 193, 7, 0.3)",
                                borderRadius: "4px",
                                padding: "8px 10px",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "6px",
                              }}
                            >
                              <Icon
                                icon="info-sign"
                                size={12}
                                style={{ color: "#F29D49", marginTop: "1px", flexShrink: 0 }}
                              />
                              <Text
                                style={{
                                  fontSize: "10px",
                                  lineHeight: "1.4",
                                  color: "var(--text-secondary)",
                                  margin: 0,
                                }}
                              >
                                <strong>Disclaimer:</strong> This is an estimate and may vary based on IOL selection and
                                special discounts.
                              </Text>
                            </div>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Consolidated total */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              icon="print"
              intent="none"
              onClick={this.printEstimate}
              disabled={!this.state.surgeryRows || this.state.surgeryRows.length === 0}
            >
              Print Estimate
            </Button>
            <Tag large intent="primary">
              Consolidated Estimate: ₹ {Number(consolidated || 0).toLocaleString("en-IN")}
            </Tag>
          </div>
        </div>
      </Modal>
    );
  };

  render() {
    return (
      <>
        <Button small icon="calculator" onClick={this.open} style={{ marginRight: 8 }}>
          Sx Price Estimator
        </Button>
        {this.renderModal()}
      </>
    );
  }
}

export default inject("authStore")(observer(SxPriceEstimator));
